import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { faCalendarClock, faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { Information } from "@/components/Information/Information";

import { useGetUserActivityQuery } from "../../../redux/analytics/analytics.api";

import { AnalyticsFilterBar } from "../components/AnalyticsFilterBar";
import { AnalyticsEmptyState } from "../components/AnalyticsEmptyState";
import { AnalyticsSkeleton } from "../components/AnalyticsSkeleton";
import { UserActivityGrid } from "../components/UserActivityGrid";
import { AnalyticsSelection } from "../hooks/useAnalyticsSelection";
import { SCHEDULED_ROW_GROUPS, SENT_ROW_GROUPS } from "../config/userActivityRows";
import { UserActivityGrid as UserGrid } from "../schema/userActivity";
import { MAX_PAST_DAYS, resolveUserActivityWindow } from "../utils/userActivityWindow";

export interface UserDeepDiveTabProps {
	selection: AnalyticsSelection;
}

const SectionCard: FunctionComponent<{
	icon: IconDefinition;
	title: string;
	hint?: string;
	children: React.ReactNode;
}> = ({ icon, title, hint, children }) => (
	<div className="rounded-xl border border-border-light bg-brand-white overflow-hidden">
		<div className="flex flex-row items-center gap-3 px-4 py-3 border-b border-border-light">
			<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-action-200 text-brand-light">
				<Icon className="w-4 h-4" icon={icon} />
			</div>
			<div className="text-sm font-semibold text-text">{title}</div>
			{hint ? <Information information={hint} /> : null}
		</div>
		{children}
	</div>
);

export const UserDeepDiveTab: FunctionComponent<UserDeepDiveTabProps> = ({ selection }) => {
	const { t } = useTranslation();

	const listIds = useMemo(
		() =>
			selection.selectedLists
				.map((list) => list.id)
				.filter((id): id is number => typeof id === "number"),
		[selection.selectedLists]
	);

	// The period cannot filter rows here — the day grid *is* the axis — so it decides how
	// many past days the grid spans instead.
	const window = useMemo(
		() => resolveUserActivityWindow(selection.dateFromToValue),
		[selection.dateFromToValue]
	);

	const { data, isLoading } = useGetUserActivityQuery({
		userIds: selection.senderUserIds,
		pastDays: window.pastDays,
		...(listIds.length > 0 ? { listIds } : {}),
	});

	const filterBar = (
		<AnalyticsFilterBar
			campaigns={selection.campaigns}
			dateFromToValue={selection.dateFromToValue}
			listOptions={selection.listOptions}
			selectedLists={selection.selectedLists}
			senderUserIds={selection.senderUserIds}
			onCampaignToggle={selection.onCampaignToggle}
			onDateChange={selection.onDateChange}
			onListContentFilterToggle={selection.onListContentFilterToggle}
			onListSelect={selection.onListSelect}
			onSenderChange={selection.onSenderChange}
			// A campaign filter has nothing to narrow: this grid counts a user's sends, and a
			// daily cap is not per campaign.
			hide={{ campaign: true }}
		/>
	);

	if (isLoading) {
		return (
			<>
				{filterBar}
				<AnalyticsSkeleton />
			</>
		);
	}

	if (!data || data.users.length === 0) {
		return (
			<>
				{filterBar}
				<AnalyticsEmptyState />
			</>
		);
	}

	const todayKey = data.past[data.past.length - 1];

	return (
		<>
			{filterBar}
			<div className="flex flex-col gap-6 w-full mt-4">
				{/* Says what the numbers cover: this tab measures sends against per-user caps,
				    so the filters do not all reach every number on it. Left unsaid, a cap that
				    does not move with the list filter reads as the filter being broken. */}
				<p className="text-xs text-text-meta" data-testid="user-scope-note">
					{t("analytics.users.scopeNote")}
				</p>

				{listIds.length > 0 ? (
					<p className="text-xs text-text-meta" data-testid="user-list-scope-note">
						{t("analytics.users.listScopeNote")}
					</p>
				) : null}

				{window.isClamped ? (
					<p className="text-xs text-text-meta" data-testid="user-period-clamp-note">
						{t("analytics.users.periodClampNote", { count: MAX_PAST_DAYS })}
					</p>
				) : null}
				{data.users.map((user: UserGrid) => (
					<div key={user.userId} className="flex flex-col gap-4">
						<div className="text-sm font-semibold text-text">{user.name}</div>

						<SectionCard
							hint={t("analytics.users.attributionHint")}
							icon={faPaperPlane}
							title={t("analytics.users.sentTitle")}
						>
							<UserActivityGrid
								caps={user.caps}
								days={data.past}
								footer={{
									labelKey: "analytics.users.rows.creditsSpent",
									byDay: data.creditSpendByDay,
								}}
								groups={SENT_ROW_GROUPS}
								todayKey={todayKey}
								values={user.sent}
							/>
						</SectionCard>

						<SectionCard icon={faCalendarClock} title={t("analytics.users.scheduledTitle")}>
							<UserActivityGrid
								caps={user.caps}
								days={data.future}
								groups={SCHEDULED_ROW_GROUPS}
								values={user.scheduled}
							/>
						</SectionCard>
					</div>
				))}

				<p className="text-xs text-text-meta">{t("analytics.users.footnote")}</p>
			</div>
		</>
	);
};
